import { z } from 'zod';
import type { AttachmentDTO, ReactionSummary } from './tasks';
import { walkRichText } from './richtext';

export const CHANNEL_TYPES = ['public', 'private', 'dm', 'group_dm'] as const;
export type ChannelType = (typeof CHANNEL_TYPES)[number];

export interface ChannelDTO {
  id: string;
  type: ChannelType;
  /** Null for direct messages: show the other members' names instead. */
  name: string | null;
  topic: string | null;
  spaceId: string | null;
  /** The office floor this channel belongs to, if any. */
  floorId: string | null;
  /** Everyone in the channel. For public channels this is who has joined. */
  memberIds: string[];
  /** Whether the current user has joined (always true for private channels and DMs they can see). */
  isMember: boolean;
  muted: boolean;
  /** Messages after the current user's read position. */
  unread: number;
  lastSeq: number;
  lastReadSeq: number;
  lastMessageAt: number | null;
  createdBy: string;
}

export interface LinkPreview {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  siteName: string | null;
}

export interface MessageDTO {
  id: string;
  channelId: string;
  /** Set for replies in a thread. */
  parentId: string | null;
  authorId: string;
  seq: number;
  body: unknown;
  preview: LinkPreview | null;
  reactions: ReactionSummary[];
  attachments: AttachmentDTO[];
  /** Tasks created from, or referenced in, the message. */
  tasks: { taskId: string; kind: 'created' | 'reference' }[];
  /** Top level messages: how many replies the thread has, who replied, and when it last did. */
  replyCount: number;
  replyUserIds: string[];
  lastReplyAt: number | null;
  editedAt: number | null;
  /** True for a deleted message that is kept as a placeholder because it has replies. */
  deleted: boolean;
  createdAt: number;
}

/** Realtime payloads on the `chat:<channelId>` topic. */
export interface ChatMessageEvent {
  message: MessageDTO;
}
export interface ChatTypingEvent {
  channelId: string;
  userId: string;
  name: string;
}

/**
 * Task ids a message refers to: `#` task mentions, and pasted task links such as
 * `/w/<workspace>/t/<taskId>` or `?task=<taskId>`.
 */
export function extractTaskRefs(doc: unknown): string[] {
  const ids = new Set<string>();
  const fromText = (value: string) => {
    for (const match of value.matchAll(/(?:\/t\/|[?&]task=)(tsk_[A-Za-z0-9]{16})/g)) ids.add(match[1]!);
  };
  walkRichText(doc, (node) => {
    if (node.type === 'taskMention' && typeof node.attrs?.id === 'string') ids.add(node.attrs.id);
    if (node.type === 'text') {
      fromText(node.text ?? '');
      for (const mark of node.marks ?? []) {
        if (mark.type === 'link' && typeof mark.attrs?.href === 'string') fromText(mark.attrs.href);
      }
    }
  });
  return [...ids];
}

/** First http(s) URL in a message that is not a link to a Regent task (those render as task cards). */
export function firstExternalUrl(doc: unknown): string | null {
  let found: string | null = null;
  const consider = (value: string) => {
    if (found) return;
    const match = /https?:\/\/[^\s<>"')]+/i.exec(value);
    if (match && !/(?:\/t\/|[?&]task=)tsk_/.test(match[0])) found = match[0].replace(/[.,;:!?]+$/, '');
  };
  walkRichText(doc, (node) => {
    if (node.type !== 'text') return;
    for (const mark of node.marks ?? []) {
      if (mark.type === 'link' && typeof mark.attrs?.href === 'string') consider(mark.attrs.href);
    }
    consider(node.text ?? '');
  });
  return found;
}

/** The ids `channel` and `everyone` in an @mention mean "all members of this channel". */
export const CHANNEL_MENTION_IDS = ['channel', 'everyone'];

// ---------- Schemas ----------

const id = z.string().min(1).max(64);
const channelName = z
  .string()
  .trim()
  .min(1, 'Name is required')
  .max(80)
  .transform((v) => v.replace(/^#+/, '').trim());

export const createChannelSchema = z.object({
  type: z.enum(['public', 'private']).default('public'),
  name: channelName,
  topic: z.string().trim().max(250).nullable().optional(),
  spaceId: id.nullable().optional(),
  /** The office floor the channel belongs to (set when a floor is created). */
  floorId: id.nullable().optional(),
  memberIds: z.array(id).max(200).optional(),
});
export const updateChannelSchema = z.object({
  name: channelName.optional(),
  topic: z.string().trim().max(250).nullable().optional(),
});
export const openDmSchema = z.object({
  /** The other people. One id opens a direct message; several open a group conversation. */
  userIds: z.array(id).min(1).max(8),
});
export const channelMembersSchema = z.object({ userIds: z.array(id).min(1).max(100) });
export const channelPrefsSchema = z.object({ muted: z.boolean() });
export const markReadSchema = z.object({ seq: z.number().int().min(0).optional() });

export const messageQuerySchema = z.object({
  /** Messages older than this sequence number (scrolling up). */
  before: z.coerce.number().int().optional(),
  /** Messages newer than this sequence number (catching up). */
  after: z.coerce.number().int().optional(),
  /** A page centered on this message (jumping to a search result or a notification). */
  around: id.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export const createMessageSchema = z.object({
  body: z.unknown(),
  parentId: id.nullable().optional(),
  attachmentIds: z.array(id).max(10).optional(),
  /** Client generated id, so an optimistic message keeps its identity. */
  id: z
    .string()
    .regex(/^msg_[A-Za-z0-9]{16}$/)
    .optional(),
});
export const updateMessageSchema = z.object({ body: z.unknown() });
export const messageToTaskSchema = z.object({ listId: id, name: z.string().trim().min(1).max(500).optional() });
export const messageSearchSchema = z.object({
  q: z.string().trim().min(1).max(200),
  channelId: id.optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});

export type CreateChannelInput = z.infer<typeof createChannelSchema>;
export type CreateMessageInput = z.infer<typeof createMessageSchema>;
